import { useEffect, useState } from "react";
import { Zap, Flame, Volume2, CheckCircle2, XCircle, Trophy, Timer, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { clip } from "@/lib/zoya";
import { answerChallenge, fetchChallenge, getLocalCh, saveLocalCh, useClassLang } from "@/lib/smart";
import { indiaDate, isStudent } from "@/lib/classroom_live";

const SECS = 20;
const LEVELS = { 1: ["Beginner", "शुरुआती"], 2: ["Intermediate", "मध्यम"], 3: ["Advanced", "एडवांस"] };
const TRACK = { robotics: ["Robotics", "रोबोटिक्स"], drones: ["Drones", "ड्रोन"], ai: ["AI", "AI"] };

const Done = ({ score, total, points, streak }) => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-8 text-center" data-testid="challenge-done">
      <Trophy className="w-12 h-12 mx-auto text-[#F5B700]" />
      <div className="mt-3 text-3xl font-black text-[#0A192F]" data-testid="challenge-score">{score}/{total}</div>
      <p className="mt-1 text-slate-600">{L("Today's challenge is complete. A new set unlocks tomorrow.", "आज का चैलेंज पूरा हुआ। कल नया सेट खुलेगा।")}</p>
      <div className="mt-4 flex justify-center gap-3 text-sm font-bold">
        {points > 0 && <span className="px-3 py-1.5 rounded-full bg-[#E0FAFF] text-[#00707F]" data-testid="challenge-points">{points} {L("game points", "गेम पॉइंट")}</span>}
        <span className="px-3 py-1.5 rounded-full bg-[#FFF3D6] text-[#7A4A00] inline-flex items-center gap-1" data-testid="challenge-streak"><Flame className="w-4 h-4" />{L(`${streak || 0}-day streak`, `${streak || 0} दिन की लगातार जीत`)}</span>
      </div>
    </div>
  );
};

export const DailyChallenge = ({ audio, profile, onProgress }) => {
  const { L } = useLang();
  const lang = useClassLang();
  const level = getLocalCh()[indiaDate()]?.level || profile?.challenge_level || 1;
  const [d, setD] = useState(null);
  const [i, setI] = useState(0);
  const [res, setRes] = useState(null);
  const [score, setScore] = useState(0);
  const [points, setPoints] = useState(0);
  const [combo, setCombo] = useState(0);
  const [left, setLeft] = useState(SECS);

  useEffect(() => {
    fetchChallenge(lang, level).then((x) => {
      const answered = isStudent() ? x.answered : getLocalCh()[x.date]?.answers || {};
      const first = x.questions.findIndex((q) => answered[q.id] === undefined);
      setD(x); setI(first < 0 ? x.questions.length : first); setScore(Object.values(answered).filter(Boolean).length);
    }).catch(() => setD(false));
  }, [lang, level]);
  useEffect(() => {
    if (!d || res) return undefined;
    setLeft(SECS);
    const t = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [d, i, res]);

  if (d === false) return <p className="text-sm text-[#C81E4B]" data-testid="challenge-error">{L("Couldn't load today's challenge. Try again.", "आज का चैलेंज लोड नहीं हुआ। फिर कोशिश करें।")}</p>;
  if (!d) return <div className="h-64 rounded-3xl bg-white/70 animate-pulse" data-testid="challenge-loading" />;
  const total = d.questions.length;
  if (i >= total) return <Done score={score} total={total} points={points} streak={profile?.streak} />;
  const q = d.questions[i];
  const pick = async (k) => {
    if (res) return;
    try {
      const r = await answerChallenge(lang, { date: d.date, qid: q.id, choice: k });
      setRes({ ...r, picked: k });
      saveLocalCh(d.date, q.id, r.correct, total, d.level);
      if (r.correct) { setScore((s) => s + 1); setPoints((p) => p + 10 + Math.ceil(left / 2) + (combo >= 1 ? 5 : 0)); setCombo((c) => c + 1); } else setCombo(0);
      audio.play(clip(r.correct ? "correct" : "wrong"));
    } catch { setD(false); }
  };
  const next = () => { setRes(null); setI(i + 1); if (i + 1 >= total) onProgress?.(); };

  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-5" data-testid="daily-challenge">
      <div className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-7">
        <div className="flex items-center justify-between gap-3 text-xs font-bold">
          <span className="text-[#007A8A] uppercase tracking-[0.16em]" data-testid="challenge-progress">{L(`Question ${i + 1} of ${total}`, `सवाल ${i + 1} / ${total}`)} · {L(...TRACK[q.track])}</span>
          <span className="inline-flex items-center gap-1 text-slate-500" data-testid="challenge-timer"><Timer className="w-4 h-4" />{left}s</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#00B8D4] transition-[width] duration-1000 ease-linear" style={{ width: `${(left / SECS) * 100}%` }} /></div>
        <div className="mt-5 flex items-start gap-3">
          <h3 className="flex-1 text-lg sm:text-xl font-extrabold text-[#0A192F] leading-snug" data-testid="challenge-question">{q.q}</h3>
          <button onClick={() => audio.say(q.q, lang)} aria-label="Read aloud" data-testid="challenge-read-btn" className="w-10 h-10 shrink-0 rounded-full grid place-items-center border border-slate-300 hover:border-[#00B8D4]"><Volume2 className="w-4 h-4" /></button>
        </div>
        <div className="mt-5 grid gap-2.5">
          {q.options.map((o, k) => {
            const state = res ? (k === res.answer ? "ok" : k === res.picked ? "bad" : "") : "";
            return (
              <button key={k} onClick={() => pick(k)} disabled={Boolean(res)} data-testid={`challenge-option-${k}`}
                className={`text-left px-4 py-3.5 rounded-2xl border-2 text-sm font-semibold transition-[border-color,background-color,transform] active:scale-[0.99]
                ${state === "ok" ? "border-emerald-500 bg-emerald-50" : state === "bad" ? "border-[#FF3B3B] bg-[#FF3B3B]/10" : "border-slate-200 hover:border-[#00B8D4]"}`}>
                <span className="inline-flex items-center gap-2">{state === "ok" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}{state === "bad" && <XCircle className="w-4 h-4 text-[#FF3B3B]" />}{o}</span>
              </button>
            );
          })}
        </div>
        {res && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="challenge-feedback">
            <p className={`text-sm font-semibold ${res.correct ? "text-emerald-700" : "text-[#C81E4B]"}`}>{res.correct ? L("Correct! ", "सही! ") : L("Not quite. ", "थोड़ा चूक गए। ")}<span className="text-slate-600 font-normal">{res.why}</span></p>
            <button onClick={next} data-testid="challenge-next-btn" className="shrink-0 inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-[#0A192F] text-white font-bold text-sm">{i + 1 >= total ? L("Finish", "पूरा करें") : L("Next", "अगला")}<ArrowRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <aside className="rounded-3xl bg-[#0A192F] text-white p-6 space-y-5">
        <div className="flex items-center gap-2 font-extrabold"><Zap className="w-5 h-5 text-[#00E5FF]" />{L("Daily challenge", "डेली चैलेंज")}</div>
        <div className="text-xs text-slate-300">{L(`Level: ${LEVELS[d.level][0]} (adapts to how you do)`, `लेवल: ${LEVELS[d.level][1]} (आपके प्रदर्शन के हिसाब से बदलता है)`)}</div>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="rounded-2xl bg-white/10 p-3"><div className="text-2xl font-black" data-testid="challenge-live-score">{score}</div><div className="text-[11px] text-slate-300">{L("correct", "सही")}</div></div>
          <div className="rounded-2xl bg-white/10 p-3"><div className="text-2xl font-black text-[#00E5FF]" data-testid="challenge-live-points">{points}</div><div className="text-[11px] text-slate-300">{L("game points", "गेम पॉइंट")}</div></div>
        </div>
        {combo >= 2 && <div className="rounded-2xl bg-[#FF2E63] px-3 py-2 text-sm font-extrabold inline-flex items-center gap-1.5" data-testid="challenge-combo"><Flame className="w-4 h-4" />{L(`Combo x${combo}!`, `कॉम्बो x${combo}!`)}</div>}
        <p className="text-[11px] text-slate-400">{L("Answer fast for bonus game points; correct answers in a row give a combo bonus. The weekly leaderboard counts 10 per correct answer + 5 for finishing the day.", "तेज़ जवाब पर बोनस गेम पॉइंट, लगातार सही जवाब पर कॉम्बो बोनस। साप्ताहिक लीडरबोर्ड में हर सही जवाब 10 + दिन पूरा करने पर 5 पॉइंट।")}</p>
      </aside>
    </div>
  );
};
